
import { useState } from "react";
import { TendenciesState, BallTendency } from "./types";
import { isIronTest, isDriverTest, isFairwayWoodTest } from "@/components/level3/scoring/utils";
import { saveTendency } from "@/components/putting/training/charts/swing/SwingChartUtils";
import { supabase } from "@/integrations/supabase/client";

export const useTendencyHandlers = (
  selectedTest: string | null,
  toast: (props: { title: string; description: string; variant?: "default" | "destructive" }) => void
) => {
  const [ballTendencies, setBallTendencies] = useState<TendenciesState>({});

  const updateTendency = async (
    point: string, 
    attempt: number, 
    tendency: BallTendency = null
  ) => {
    console.log(`updateTendency called: point=${point}, attempt=${attempt}, tendency=${tendency}, selectedTest=${selectedTest}`);
    
    if (selectedTest && (isIronTest(selectedTest) || isDriverTest(selectedTest) || isFairwayWoodTest(selectedTest))) {
      // Update state for UI
      setBallTendencies(prev => {
        const newTendencies = { ...prev };
        if (!newTendencies[point]) {
          newTendencies[point] = Array(10).fill(null);
        }
        newTendencies[point][attempt] = tendency;
        console.log(`Updated ballTendencies for ${point}[${attempt}]:`, tendency);
        return newTendencies;
      });
      
      // If there's a valid tendency, save it to the database
      if (tendency && (tendency === 'left' || tendency === 'center' || tendency === 'right')) {
        try {
          console.log(`Saving tendency to database: ${tendency}`);
          
          // Get current user
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user) {
            console.error("Aucun utilisateur authentifié trouvé lors de l'enregistrement de la tendance");
            return;
          }
          
          // Determine club type and distance
          let clubType: string;
          let distance: string;
          
          if (isIronTest(selectedTest)) {
            clubType = 'iron';
            // Map point to distance
            const pointToDistance: Record<string, string> = {
              'IronA': '110m',
              'IronB': '130m',
              'IronC': '150m'
            };
            distance = pointToDistance[point] || '130m';
          } else if (isFairwayWoodTest(selectedTest)) {
            clubType = 'fairwayWood';
            // Map point to distance
            const pointToDistance: Record<string, string> = {
              'FairwayWoodA': '170m',
              'FairwayWoodB': '180m',
              'FairwayWoodC': '200m'
            };
            distance = pointToDistance[point] || '180m';
          } else if (isDriverTest(selectedTest)) {
            clubType = 'driver';
            // For driver tests, we use accuracy as distance
            distance = 'accuracy';
          } else {
            console.log(`Test type not supported for tendency tracking: ${selectedTest}`);
            return;
          }
          
          // Déterminer le niveau en fonction du contexte
          let level: string = '3'; // Default to level 3
          
          // 1. Essayer d'extraire le niveau à partir de l'ID du test (ex: "iron-l2" -> "2")
          const levelMatch = selectedTest.match(/-l(\d+)/i);
          if (levelMatch && levelMatch[1]) {
            level = levelMatch[1];
            console.log(`Niveau extrait de l'ID du test: ${level}`);
          } else {
            // 2. Déterminer le niveau en fonction de la page actuelle
            const pathname = window.location.pathname;
            
            if (pathname.includes('level1')) {
              level = '1';
            } else if (pathname.includes('level2')) {
              level = '2';
            } else if (pathname.includes('level3') || pathname.includes('driver')) {
              level = '3';
            }
            
            console.log(`Niveau déterminé à partir du chemin: ${level}`);
          }
          
          console.log(`Enregistrement de la tendance: clubType=${clubType}, distance=${distance}, tendency=${tendency}, level=${level}`);
          
          // Save tendency to database
          await saveTendency(
            session.user.id,
            selectedTest,
            clubType,
            distance,
            tendency,
            level
          );
          
          console.log(`Tendance ${clubType} enregistrée avec succès pour ${distance}: ${tendency} (niveau: ${level})`);
        } catch (error) {
          console.error("Erreur lors de l'enregistrement de la tendance:", error);
        }
      }
    }
  };

  const resetTendencies = (testId: string, points: string[]) => {
    if (isIronTest(testId) || isDriverTest(testId) || isFairwayWoodTest(testId)) {
      const newTendencies: TendenciesState = {};
      points.forEach(point => {
        newTendencies[point] = Array(10).fill(null);
      });
      setBallTendencies(newTendencies);
    } else {
      setBallTendencies({});
    }
  };
  
  const analyzeTendencies = () => {
    if (!selectedTest || !(isIronTest(selectedTest) || isDriverTest(selectedTest) || isFairwayWoodTest(selectedTest))) {
      return null;
    }
    
    console.log("Analyse des tendances de balles pour le test:", selectedTest, ballTendencies);
    
    const allTendencies = Object.values(ballTendencies).flatMap(tendencies => 
      tendencies.filter((t): t is 'left' | 'right' | 'center' => t !== null)
    );
    
    const leftCount = allTendencies.filter(t => t === 'left').length;
    const rightCount = allTendencies.filter(t => t === 'right').length;
    const centerCount = allTendencies.filter(t => t === 'center').length;
    
    let tendencyMessage = "";
    
    if (leftCount > rightCount && leftCount > centerCount) {
      tendencyMessage = "Tendance générale: balles qui partent à gauche. Travaillez votre chemin de club pour corriger.";
    } else if (rightCount > leftCount && rightCount > centerCount) {
      tendencyMessage = "Tendance générale: balles qui partent à droite. Vérifiez votre alignement et votre face de club.";
    } else if (centerCount > leftCount && centerCount > rightCount) {
      tendencyMessage = "Bonne précision! La plupart de vos balles sont au centre.";
    } else {
      tendencyMessage = "Pas de tendance claire identifiée.";
    }
    
    console.log("Message de tendance généré:", tendencyMessage);
    return tendencyMessage;
  };

  return {
    ballTendencies,
    setBallTendencies,
    updateTendency,
    resetTendencies,
    analyzeTendencies
  };
};
